import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { journal as journalApi, tags as tagsApi, sessions as sessionsApi } from '../api/client';
import TagFilterPills from '../components/TagFilterPills';

function formatPrice(p) {
  if (p == null) return '—';
  return parseFloat(p).toFixed(5);
}

function formatPnl(pnl) {
  if (pnl == null) return '—';
  const n = parseFloat(pnl);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}`;
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function JournalPage() {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  // Filters
  const [direction, setDirection] = useState('');
  const [result, setResult] = useState('');
  const [instrument, setInstrument] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [challengeFilter, setChallengeFilter] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const [allSessions, setAllSessions] = useState([]);
  const [instruments, setInstruments] = useState([]);

  // Load tags and derive instruments from sessions
  useEffect(() => {
    tagsApi.list().then((res) => setAllTags(res.data)).catch(() => {});
    sessionsApi.list().then((res) => {
      const ins = [...new Set(res.data.map((s) => s.instrument))].sort();
      setInstruments(ins);
      setAllSessions(res.data);
    }).catch(() => {});
  }, []);

  const fetchTrades = useCallback(
    (newOffset = 0, append = false) => {
      setLoading(true);
      const params = {
        status: 'closed',
        limit: LIMIT,
        offset: newOffset,
      };
      if (direction) params.direction = direction;
      if (result) params.result = result;
      if (instrument) params.instrument = instrument;
      if (selectedTags.length > 0) params.tags = selectedTags.join(',');
      if (challengeFilter) params.challenge = challengeFilter;
      if (sessionFilter) params.session_id = sessionFilter;

      journalApi.list(params).then((res) => {
        const data = res.data.results || res.data;
        if (append) {
          setTrades((prev) => [...prev, ...data]);
        } else {
          setTrades(data);
        }
        setHasMore(!!res.data.next);
        setOffset(newOffset);
      }).catch((err) => {
        console.error('Failed to load journal:', err);
      }).finally(() => setLoading(false));
    },
    [direction, result, instrument, selectedTags, challengeFilter, sessionFilter]
  );

  useEffect(() => {
    fetchTrades(0);
  }, [fetchTrades]);

  const loadMore = () => {
    fetchTrades(offset + LIMIT, true);
  };

  const clearFilters = () => {
    setDirection('');
    setResult('');
    setInstrument('');
    setSelectedTags([]);
    setChallengeFilter('');
    setSessionFilter('');
  };

  // Summary stats from loaded trades
  const totalPnl = trades.reduce((s, t) => s + parseFloat(t.pnl || 0), 0);
  const wins = trades.filter((t) => parseFloat(t.pnl) > 0).length;
  const winRate = trades.length > 0 ? ((wins / trades.length) * 100).toFixed(1) : '—';

  return (
    <div className="page journal-page">
      <div className="page-header">
        <h1>Trading Journal</h1>
      </div>

      {/* Summary bar */}
      <div className="journal-summary">
        <span>Trades: <strong>{trades.length}</strong></span>
        <span>Win Rate: <strong>{winRate}%</strong></span>
        <span>
          Total P&L:{' '}
          <strong className={totalPnl > 0 ? 'pnl-positive' : totalPnl < 0 ? 'pnl-negative' : ''}>
            {totalPnl > 0 ? '+' : ''}{totalPnl.toFixed(2)}
          </strong>
        </span>
      </div>

      {/* Filters */}
      <div className="journal-filters">
        <div className="filter-group">
          <label>Instrument</label>
          <select value={instrument} onChange={(e) => setInstrument(e.target.value)}>
            <option value="">All</option>
            {instruments.map((ins) => (
              <option key={ins} value={ins}>{ins}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Direction</label>
          <div className="filter-toggles">
            {['', 'buy', 'sell'].map((v) => (
              <button
                key={v}
                className={`filter-toggle ${direction === v ? 'active' : ''}`}
                onClick={() => setDirection(v)}
              >
                {v || 'All'}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label>Result</label>
          <div className="filter-toggles">
            {['', 'win', 'loss', 'breakeven'].map((v) => (
              <button
                key={v}
                className={`filter-toggle ${result === v ? 'active' : ''}`}
                onClick={() => setResult(v)}
              >
                {v || 'All'}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label>Tags</label>
          <TagFilterPills
            tags={allTags}
            selectedTagIds={selectedTags}
            onChange={setSelectedTags}
          />
        </div>

        <div className="filter-group">
          <label>Session</label>
          <select value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)}>
            <option value="">All Sessions</option>
            {allSessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || `Session #${s.id}`} — {s.instrument}
                {s.challenge_config ? ` (${s.challenge_config.name})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Type</label>
          <div className="filter-toggles">
            {[['', 'All'], ['yes', 'Challenge'], ['no', 'Free']].map(([v, label]) => (
              <button key={v}
                className={`filter-toggle ${challengeFilter === v ? 'active' : ''}`}
                onClick={() => setChallengeFilter(v)}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <button className="btn btn-sm" onClick={clearFilters}>Clear</button>
      </div>

      {/* Trade list */}
      {trades.length === 0 && !loading ? (
        <div className="empty-state">No trades found.</div>
      ) : (
        <table className="journal-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Instrument</th>
              <th>Dir</th>
              <th>Volume</th>
              <th>Entry</th>
              <th>Exit</th>
              <th>P&L</th>
              <th>Reason</th>
              <th>Tags</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {trades.map((trade) => {
              const pnl = parseFloat(trade.pnl || 0);
              const pnlClass = pnl > 0 ? 'pnl-positive' : pnl < 0 ? 'pnl-negative' : '';
              return (
                <tr
                  key={trade.id}
                  className="journal-row"
                  onClick={() => navigate(`/journal/trade/${trade.session}/${trade.id}`)}
                >
                  <td>{formatDate(trade.entry_timestamp)}</td>
                  <td>
                    {trade.session_instrument}
                    {trade.playbook && <span className="journal-pb-dot" title="Playbook trade">P</span>}
                  </td>
                  <td>
                    <span className={`dir-badge dir-${trade.direction}`}>
                      {trade.direction?.toUpperCase()}
                    </span>
                  </td>
                  <td>{trade.volume}</td>
                  <td>{formatPrice(trade.entry_price)}</td>
                  <td>{formatPrice(trade.exit_price)}</td>
                  <td className={pnlClass}>{formatPnl(trade.pnl)}</td>
                  <td className="positions-reason">{trade.exit_reason}</td>
                  <td>
                    <div className="journal-tags">
                      {(trade.tags || []).map((tag) => (
                        <span key={tag.id} className="tag-mini" style={{ borderColor: tag.color }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {trade.note_count > 0 && (
                      <span className="journal-note-badge">{trade.note_count}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {loading && <div className="loading" style={{ height: 'auto', padding: '1rem' }}>Loading...</div>}

      {hasMore && !loading && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button className="btn btn-sm" onClick={loadMore}>Load more</button>
        </div>
      )}
    </div>
  );
}
